import { Button, IconButton, Typography } from '@neo4j-ndl/react';
import { SparklesIconOutline, UserPlusIconOutline } from '@neo4j-ndl/react/icons';
import { PLG_EVENTS } from '@nx/analytics-service';
import { APP_SCOPE } from '@nx/constants';
import { ACTION, MODAL_TYPE, useActiveProject, useAuraUser, useModal, usePermissions, useTrackEvent } from '@nx/state';
import { PermissionTooltip } from '@nx/ui';

type HeaderButtonProps = {
  icon: React.ReactNode;
  label: string;
  href?: string;
  onClick?: () => void;
  isDisabled?: boolean;
};

const HeaderButton = ({ icon, href, label, onClick, isDisabled }: HeaderButtonProps) => {
  return (
    <>
      <Button
        className="@sm:flex hidden shrink-0 flex-row items-center gap-2"
        size="small"
        color="neutral"
        fill="outlined"
        as="a"
        href={href}
        target="_blank"
        htmlAttributes={{
          rel: 'noreferrer',
        }}
        onClick={onClick}
        isDisabled={isDisabled}
      >
        {icon}
        <span className="shrink-0">{label}</span>
      </Button>
      <IconButton
        className="@sm:hidden"
        ariaLabel={label}
        onClick={onClick}
        isDisabled={isDisabled}
        htmlAttributes={{
          rel: 'noreferrer',
          href,
          target: '_blank',
        }}
        as="a"
      >
        {icon}
      </IconButton>
    </>
  );
};

const Header = () => {
  const activeProject = useActiveProject();
  const { user } = useAuraUser();
  const track = useTrackEvent();
  const projectInviteModal = useModal(MODAL_TYPE.PROJECT_INVITE);
  const { isAllowed } = usePermissions();
  const allowInvite = isAllowed(ACTION.CREATE, `namespaces/${activeProject.id}/invites`);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h1 className="inline-block">Developer hub</h1>
        <div className="flex gap-2">
          <PermissionTooltip hasPermission={allowInvite}>
            <HeaderButton
              icon={<UserPlusIconOutline className="@sm:size-4 shrink-0" />}
              label="Invite your team"
              onClick={() => {
                projectInviteModal.open({ project: activeProject });
                track({
                  event: PLG_EVENTS.DEVELOPER_HUB_BUTTON,
                  scope: APP_SCOPE.aura,
                  properties: {
                    button: 'invite-user',
                    org_id: activeProject.organizationId,
                    user_id: user?.id,
                  },
                });
              }}
              isDisabled={!allowInvite}
            />
          </PermissionTooltip>
          <HeaderButton
            icon={<SparklesIconOutline className="@sm:size-4 shrink-0" />}
            label="Ask Neo4j AI"
            href="https://neo4j.com/docs/create-applications/?chatbot=open"
            onClick={() => {
              track({
                event: PLG_EVENTS.DEVELOPER_HUB_LINK,
                scope: APP_SCOPE.aura,
                properties: {
                  link: 'docs',
                  org_id: activeProject.organizationId,
                  user_id: user?.id,
                },
              });
            }}
          />
        </div>
      </div>
      <Typography variant="body-medium" className="text-neutral-text-weak tracking-normal">
        Discover the client libraries, APIs and useful resources to develop applications with Neo4j
      </Typography>
    </div>
  );
};

export default Header;
